
import React, { useState, useMemo, useEffect } from "react";
import {
    Box,
    Stack,
    Autocomplete,
    TextField,
    Popover,
    Snackbar,
    Alert,
    Divider,
} from "@mui/material";
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import CalendarMonthIcon from "../../../assets/images/Calendar.svg";
import dayjs from "dayjs";
import CustomButton from "../../components/buttons/CustomButton";
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import localeData from 'dayjs/plugin/localeData';

dayjs.extend(isSameOrAfter);
dayjs.extend(localeData);

const months = [
    "January", "February", "March", "April", "May", "June",
    "July", "August", "September", "October", "November", "December"
];

const getMonthNumber = (monthName) => {
    const index = months.indexOf(monthName);
    return index === -1 ? "01" : String(index + 1).padStart(2, "0");
};

const generateYears = () => {
    const startYear = 2025;
    const currentYear = dayjs().year();
    const yearsArray = [];

    for (let y = startYear; y <= currentYear; y++) {
        yearsArray.push(`${y}`);
    }

    // Unlock next year only if we reach Dec 30th of current year or beyond
    if (dayjs().isSameOrAfter(dayjs(`${currentYear}-12-30`))) {
        yearsArray.push(`${currentYear + 1}`);
    }

    return yearsArray;
};

const isFromDateDisabled = true;

const DateSelector = ({ label, date, setDate, disabled = false }) => {
    const selectedYear = parseInt(date.year);
    const selectedMonthIndex = months.indexOf(date.month);
    const today = dayjs();

    const allowedMonths = (selectedYear === today.year())
        ? months.slice(0, today.month() + 1)
        : months;

    const allowedDays = useMemo(() => {
        let daysInMonth = 31;

        if (selectedYear && selectedMonthIndex >= 0) {
            const monthNum = String(selectedMonthIndex + 1).padStart(2, '0');
            daysInMonth = dayjs(`${selectedYear}-${monthNum}-01`).daysInMonth();
        }

        let validDays = Array.from({ length: daysInMonth }, (_, i) => (i + 1).toString().padStart(2, "0"));

        if (selectedYear === today.year() && selectedMonthIndex === today.month()) {
            validDays = validDays.filter(day => parseInt(day) <= today.date());
        }

        return validDays;
    }, [selectedYear, selectedMonthIndex]);

    const availableYears = generateYears();

    return (
        <Box>
            <span style={{ fontSize: "11px", fontWeight: "500", color: "#2B2B2B", paddingBottom: "8px" }}>{label}</span>
            <Stack direction="row" spacing={1}>
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        border: '1px solid #949495',
                        borderRadius: '8px',
                        overflow: 'hidden',
                        minWidth: '100%',
                        height: '30%',
                        pointerEvents: disabled ? 'none' : 'auto',
                        opacity: disabled ? 0.5 : 1,
                    }}
                >
                    <Autocomplete
                        size="small"
                        disableClearable
                        disabled={disabled}
                        options={allowedDays}
                        value={allowedDays.includes(date.day) ? date.day : allowedDays[0]}
                        onChange={(_, value) => setDate(prev => ({ ...prev, day: value }))}

                        popupIcon={<KeyboardArrowDownIcon sx={{ fontSize: '12px', pointerEvents: 'none' }} />}
                        sx={{ width: 50, '& .MuiOutlinedInput-root': { border: 'none', borderRadius: 0, '& fieldset': { border: 'none' } } }}
                        ListboxProps={{
                            style: {
                                maxHeight: 100, // Reduce dropdown height
                                fontSize: '12px',
                                padding: 0,
                            }
                        }}
                        PaperComponent={({ children }) => (
                            <div style={{
                                margin: 0,
                                fontSize: '12px',
                                background: '#ffffff',
                                boxShadow: '0px 2px 8px rgba(0,0,0,0.12)',
                                padding: 0,
                            }}>{children}</div>
                        )}
                        renderInput={(params) => (
                            <TextField {...params} placeholder="01" InputProps={{
                                ...params.InputProps,
                                sx: { ...params.InputProps?.sx, fontSize: '10px', fontWeight: '400', color: '#000' },
                            }} inputProps={{
                                ...params.inputProps,
                                style: { ...(params.inputProps?.style || {}), fontSize: '12px' },
                            }} />
                        )}
                    />
                    <Divider orientation="vertical" flexItem sx={{ color: "#949495" }} />
                    <Autocomplete
                        size="small"
                        disableClearable
                        disabled={disabled}
                        options={allowedMonths}
                        value={allowedMonths.includes(date.month) ? date.month : allowedMonths[0]}
                        onChange={(_, value) => {
                            const selectedYear = parseInt(date.year);
                            const selectedMonthIndex = months.indexOf(value);
                            const daysInMonth = dayjs(`${selectedYear}-${String(selectedMonthIndex + 1).padStart(2, '0')}-01`).daysInMonth();
                            // If current day is greater than days in new month, reset to "01"
                            setDate(prev => ({
                                ...prev,
                                month: value,
                                day: parseInt(prev.day) > daysInMonth ? "01" : prev.day
                            }));
                        }}
                        popupIcon={<KeyboardArrowDownIcon sx={{ fontSize: '12px', pointerEvents: 'none' }} />}
                        sx={{ width: 90, '& .MuiOutlinedInput-root': { border: 'none', borderRadius: 0, '& fieldset': { border: 'none' } } }}
                        ListboxProps={{
                            style: {
                                maxHeight: 120, // Reduce dropdown height
                                fontSize: '12px',
                                padding: 0,
                            }
                        }}
                        PaperComponent={({ children }) => (
                            <div style={{
                                margin: 0,
                                fontSize: '12px',
                                background: '#ffffff',
                                boxShadow: '0px 2px 8px rgba(0,0,0,0.12)',
                                padding: 0,
                            }}>{children}</div>
                        )}
                        renderInput={(params) => (
                            <TextField {...params} placeholder="Month" InputProps={{
                                ...params.InputProps,
                                sx: { ...params.InputProps?.sx, fontSize: '10px', fontWeight: '400', color: '#000' },
                            }} inputProps={{
                                ...params.inputProps,
                                style: { ...(params.inputProps?.style || {}), fontSize: '12px' },
                            }} />
                        )}
                    />
                    <Divider orientation="vertical" flexItem />
                    <Autocomplete
                        size="small"
                        disableClearable
                        disabled={disabled}
                        options={availableYears}
                        value={availableYears.includes(date.year) ? date.year : availableYears[0]}
                        onChange={(_, value) => setDate(prev => ({ ...prev, year: value }))}
                        popupIcon={<KeyboardArrowDownIcon sx={{ fontSize: '12px', pointerEvents: 'none' }} />}
                        sx={{ width: 70, '& .MuiOutlinedInput-root': { border: 'none', borderRadius: 0, '& fieldset': { border: 'none' } } }}
                        ListboxProps={{
                            style: {
                                maxHeight: 120, // Reduce dropdown height
                                fontSize: '12px',
                                padding: 0,
                            }
                        }}
                        PaperComponent={({ children }) => (
                            <div style={{
                                margin: 0,
                                fontSize: '12px',
                                background: '#ffffff',
                                boxShadow: '0px 2px 8px rgba(0,0,0,0.12)',
                                padding: 0,
                            }}>{children}</div>
                        )}
                        renderInput={(params) => (
                            <TextField {...params} placeholder={`${dayjs().year()}`} InputProps={{
                                ...params.InputProps,
                                sx: { ...params.InputProps?.sx, fontSize: '10px', fontWeight: '400', color: '#000' },
                            }} inputProps={{
                                ...params.inputProps,
                                style: { ...(params.inputProps?.style || {}), fontSize: '12px' },
                            }} />
                        )}
                    />
                </Box>
            </Stack>
        </Box>
    );
};

const formatDisplayDate = (dateObj) => {
    if (!dateObj.day || !dateObj.month || !dateObj.year) return "";
    const monthNum = getMonthNumber(dateObj.month);
    const formattedDate = dayjs(`${dateObj.year}-${monthNum}-${dateObj.day}`);
    return formattedDate.isValid() ? formattedDate.format("DD MMMM YYYY") : "";
};

const DateRangePicker = ({ value, onChange }) => {
    const [anchorEl, setAnchorEl] = useState(null);
    // Defaults: prefer explicit 2026 for ORLT default view
    // const [fromDate, setFromDate] = useState({ day: "01", month: "January", year: "2025" });
    // const [toDate, setToDate] = useState({ day: "01", month: "January", year: `${dayjs().year()}` });
    const [snackbarOpen, setSnackbarOpen] = useState(false);
    const [snackbarMsg, setSnackbarMsg] = useState("");

    const [fromDate, setFromDate] = useState(value?.fromDate || { day: "01", month: "January", year: String(dayjs().year()) });
    const [toDate, setToDate] = useState(value?.toDate || { day: "01", month: "January", year: String(dayjs().year()) });

    const fromFormatted = useMemo(() => formatDisplayDate(fromDate), [fromDate]);
    const toFormatted = useMemo(() => formatDisplayDate(toDate), [toDate]);

    const handleSetDates = () => {
        const from = dayjs(`${fromDate.year}-${getMonthNumber(fromDate.month)}-${fromDate.day}`);
        const to = dayjs(`${toDate.year}-${getMonthNumber(toDate.month)}-${toDate.day}`);
        const today = dayjs();

        if (!from.isValid() || !to.isValid()) {
            setSnackbarMsg("Invalid dates selected.");
            setSnackbarOpen(true);
            return;
        }

        if (to.isBefore(from)) {
            setSnackbarMsg("To date cannot be earlier than From date.");
            setSnackbarOpen(true);
            return;
        }

        if (to.isAfter(today)) {
            setSnackbarMsg("To date cannot be in the future.");
            setSnackbarOpen(true);
            return;
        }
        // Pass valid date range to parent
        onChange({

            from: from.format('YYYY-MM-DD'),
            to: to.format('YYYY-MM-DD'),

        });

        setAnchorEl(null);
    };

    useEffect(() => {
        if (value?.fromDate) setFromDate(value.fromDate);
        if (value?.toDate) setToDate(value.toDate);
    }, [value]);


    return (
        <Box>
            <Box
                onClick={(e) => {
                    setAnchorEl(e.currentTarget);
                }}
                sx={{
                    border: "1px solid #ccc",
                    borderRadius: 1,
                    px: 1,
                    minWidth: "220px",
                    width: "auto",
                    cursor: "pointer",
                    display: "flex",
                    height: "37px",
                    alignItems: "center",
                    justifyContent: "space-between",
                    backgroundColor: "#ffffff",
                }}
            >
                <Stack direction="row" spacing={2} alignItems="center">
                    <img alt="Calendar" src={CalendarMonthIcon} style={{ width: "10%", height: "10%" }} />
                    <Stack spacing={0.5}>
                        <span style={{ fontSize: '10px', fontWeight: '400', color: '#949495' }}>From date</span>
                        <span style={{ fontSize: '10px', fontWeight: '400', color: isFromDateDisabled ? '#B0B0B0' : '#2B2B2B', whiteSpace: 'nowrap' }}>{fromFormatted || "Select From Date"}</span>
                    </Stack>
                    <Stack spacing={0.5}>
                        <span style={{ fontSize: '10px', fontWeight: '400', color: '#949495' }}>To date</span>
                        <span style={{ fontSize: '10px', fontWeight: '400', color: '#2B2B2B', whiteSpace: 'nowrap' }}>{toFormatted || "Select To Date"}</span>
                    </Stack>
                </Stack>
                <KeyboardArrowDownIcon fontSize="10px" fontWeight="500" />
            </Box>

            <Popover
                open={Boolean(anchorEl)}
                anchorEl={anchorEl}
                onClose={() => setAnchorEl(null)}
                anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
                PaperProps={{ sx: { p: 0, borderRadius: 2, boxShadow: 3, width: 234, height: 250 } }}
            >
                <Box sx={{ backgroundColor: "#005EEF33", p: 1, borderTopLeftRadius: 8, borderTopRightRadius: 8 }}>
                    <span style={{ fontSize: '11px', fontWeight: '400', display: 'flex', justifyContent: 'center' }}>Date range</span>
                </Box>
                <Box sx={{ p: 2 }}>
                    <DateSelector label="From" date={fromDate} setDate={setFromDate} disabled={true} />
                    <Box mt={2}>
                        <DateSelector label="To" date={toDate} setDate={setToDate} />
                    </Box>
                    <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", pt: 1 }}>
                        <CustomButton label="Set Dates" type="date" onClick={handleSetDates} />
                    </Box>
                </Box>
            </Popover>

            <Snackbar
                open={snackbarOpen}
                autoHideDuration={3000}
                onClose={() => setSnackbarOpen(false)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert onClose={() => setSnackbarOpen(false)} severity="error" sx={{ width: '100%' }}>
                    {snackbarMsg}
                </Alert>
            </Snackbar>
        </Box>
    );
};

export default DateRangePicker;

